import { useState } from "react";
import {
  useGetAdminApplications,
  useUpdateApplicationStatus,
  getGetAdminApplicationsQueryKey,
  type RaiserApplication,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
} from "@workspace/ui-web/components/ui/card";
import { Badge } from "@workspace/ui-web/components/ui/badge";
import { Button } from "@workspace/ui-web/components/ui/button";
import { Input } from "@workspace/ui-web/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui-web/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@workspace/ui-web/components/ui/tabs";
import { useToast } from "@workspace/ui-web/hooks/use-toast";
import {
  ClipboardList,
  Search,
  ChevronDown,
  ChevronUp,
  Loader2,
  Printer,
} from "lucide-react";
import { format } from "date-fns";
import { printApplication } from "./printApplication";

type AppStatus = "new" | "under_review" | "accepted" | "declined";

const STATUS_LABELS: Record<AppStatus, string> = {
  new: "New",
  under_review: "Under Review",
  accepted: "Accepted",
  declined: "Declined",
};

const STATUS_BADGE_VARIANT: Record<
  AppStatus,
  "default" | "secondary" | "outline" | "destructive"
> = {
  new: "default",
  under_review: "secondary",
  accepted: "outline",
  declined: "destructive",
};

const RESIDENCE_LABELS: Record<string, string> = {
  house: "House",
  townhouse: "Townhouse",
  apartment_condo: "Apartment / Condo",
  dorm: "Dorm",
  other: "Other",
};

const WORK_SCHOOL_LABELS: Record<string, string> = {
  work: "Yes, to work",
  school: "Yes, to school",
  both: "Yes, to both",
  neither: "No",
};

type FieldDef = {
  key: keyof RaiserApplication;
  label: string;
  map?: Record<string, string>;
};

const SECTIONS: { title: string; fields: FieldDef[] }[] = [
  {
    title: "General Information",
    fields: [
      { key: "fullName", label: "Full name" },
      { key: "email", label: "Email" },
      { key: "address", label: "Street address" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "zip", label: "ZIP" },
      { key: "cellPhone", label: "Cell phone" },
      { key: "daytimePhone", label: "Daytime phone" },
      { key: "eveningPhone", label: "Evening phone" },
      { key: "recoveryConnection", label: "Connection to recovery community" },
      { key: "howHeard", label: "How they heard about us" },
      { key: "whyRaiser", label: "Why they want to be a raiser" },
    ],
  },
  {
    title: "Handlers & Household",
    fields: [
      { key: "primaryHandlerName", label: "Primary handler" },
      { key: "primaryHandlerAge", label: "Primary handler age" },
      { key: "primaryHandlerOccupation", label: "Primary handler occupation" },
      { key: "secondaryHandlerName", label: "Secondary handler" },
      { key: "secondaryHandlerAge", label: "Secondary handler age" },
      {
        key: "secondaryHandlerOccupation",
        label: "Secondary handler occupation",
      },
      { key: "householdMembers", label: "Other household members" },
    ],
  },
  {
    title: "Typical Day & Schedule",
    fields: [
      { key: "typicalWeekday", label: "Typical weekday" },
      { key: "typicalWeekend", label: "Typical weekend" },
    ],
  },
  {
    title: "Dog Experience & Current Pets",
    fields: [
      { key: "ownedDogsBefore", label: "Has owned a dog" },
      { key: "ownedDogsDetails", label: "When / breeds" },
      { key: "raisedPuppy", label: "Has raised a puppy" },
      { key: "pottyTrainedPuppy", label: "Has potty trained a puppy" },
      { key: "crateTrainedPuppy", label: "Has crate trained a puppy" },
      { key: "currentPets", label: "Currently owns pets" },
      { key: "currentPetsDetails", label: "Current pets" },
      { key: "petsSpayedNeutered", label: "Pets spayed/neutered" },
      { key: "petsVaccinationProof", label: "Can provide vaccination proof" },
      { key: "vetName", label: "Veterinarian" },
      { key: "vetPhone", label: "Veterinarian phone" },
      { key: "petProblemBehaviors", label: "Pets with problem behaviors" },
      { key: "petProblemBehaviorsDetails", label: "Problem behavior details" },
      { key: "trainingClasses", label: "Attended training classes" },
      { key: "trainingClassesDetails", label: "Training class details" },
    ],
  },
  {
    title: "Home",
    fields: [
      { key: "residenceType", label: "Residence type", map: RESIDENCE_LABELS },
      { key: "residenceTypeOther", label: "Residence type (other)" },
      {
        key: "ownOrRent",
        label: "Own or rent",
        map: { own: "Own", rent: "Rent" },
      },
      { key: "landlordPermission", label: "Landlord permission" },
      { key: "landlordName", label: "Landlord name" },
      { key: "landlordPhone", label: "Landlord phone" },
      { key: "hasYard", label: "Has yard" },
      { key: "yardFenced", label: "Yard fully fenced" },
      { key: "fenceDescription", label: "Fence type / height" },
      { key: "hasPool", label: "Has pool" },
      { key: "poolFenced", label: "Pool fenced" },
      {
        key: "neighborhoodDogInteraction",
        label: "Expected neighborhood dog interaction",
      },
    ],
  },
  {
    title: "Life with a Puppy",
    fields: [
      { key: "acceptDamageRisk", label: "Accepts damage risk" },
      { key: "acknowledgeAccidents", label: "Acknowledges accidents" },
      {
        key: "planWorkSchool",
        label: "Plans to take dog to work/school",
        map: WORK_SCHOOL_LABELS,
      },
      { key: "workSchoolPermission", label: "Has permission at work/school" },
      { key: "workSchoolDaysHours", label: "Days / hours" },
      { key: "workSchoolLocation", label: "Work / school location" },
      { key: "workSchoolEnvironment", label: "Work / school environment" },
      { key: "aloneHours", label: "Hours dog would be alone" },
      { key: "aloneLocation", label: "Where dog stays when alone" },
      { key: "situationsNotTake", label: "Situations dog would not go" },
      { key: "sleepingArrangements", label: "Sleeping arrangements" },
      { key: "exercisePlan", label: "Exercise plan" },
      { key: "publicOutingsCommitment", label: "Committed to public outings" },
      { key: "publicOutingsExplain", label: "Public outings explanation" },
      { key: "smokes", label: "Smokes" },
      {
        key: "refrainFromSmoking",
        label: "Will refrain from smoking near dog",
      },
      { key: "commitTrainingSessions", label: "Committed to training" },
      { key: "commitReports", label: "Committed to reports" },
      { key: "consentHomeVisits", label: "Consents to home visits" },
      { key: "anyGenderBreed", label: "Willing to raise any gender/breed" },
      { key: "anyGenderBreedExplain", label: "Gender/breed explanation" },
    ],
  },
  {
    title: "Reference",
    fields: [
      { key: "referenceName", label: "Name" },
      { key: "referenceRelationship", label: "Relationship" },
      { key: "referencePhone", label: "Phone" },
      { key: "referenceEmail", label: "Email" },
      { key: "referenceAddress", label: "Address" },
    ],
  },
  {
    title: "Agreement",
    fields: [
      { key: "agreeEmergencyCare", label: "Authorizes emergency vet care" },
      { key: "agreeRiskAcknowledgment", label: "Acknowledges volunteer risks" },
      { key: "agreeTruthful", label: "Confirms answers are truthful" },
      { key: "signatureName", label: "Signed (typed name)" },
    ],
  },
];

function formatValue(value: unknown, map?: Record<string, string>): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.map((v) => map?.[String(v)] ?? String(v)).join(", ");
  }
  return map?.[String(value)] ?? String(value);
}

function ApplicationDetail({ app }: { app: RaiserApplication }) {
  return (
    <div className="space-y-5">
      {SECTIONS.map((section) => (
        <div key={section.title}>
          <h4 className="text-sm font-semibold text-primary mb-2">
            {section.title}
          </h4>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {section.fields.map((f) => (
              <div key={String(f.key)} className="text-sm">
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="text-foreground whitespace-pre-wrap">
                  {formatValue(app[f.key], f.map)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

export default function AdminApplications() {
  const { data: applications, isLoading } = useGetAdminApplications();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const updateStatus = useUpdateApplicationStatus();
  const [statusFilter, setStatusFilter] = useState<"all" | AppStatus>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const handleStatusChange = (id: number, status: AppStatus) => {
    updateStatus.mutate(
      { id, data: { status } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetAdminApplicationsQueryKey(),
          });
          toast({
            title: "Status updated",
            description: `Application marked as ${STATUS_LABELS[status].toLowerCase()}.`,
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update application status.",
            variant: "destructive",
          });
        },
      },
    );
  };

  const handlePrint = (app: RaiserApplication) => {
    const opened = printApplication(app, SECTIONS);
    if (!opened) {
      toast({
        title: "Pop-up blocked",
        description:
          "Please allow pop-ups for this site to print the application.",
        variant: "destructive",
      });
    }
  };

  const newCount = applications?.filter((a) => a.status === "new").length ?? 0;

  const query = searchQuery.trim().toLowerCase();
  const filtered = (applications ?? [])
    .filter((a) => statusFilter === "all" || a.status === statusFilter)
    .filter((a) => {
      if (!query) return true;
      return (
        a.fullName.toLowerCase().includes(query) ||
        a.email.toLowerCase().includes(query) ||
        (a.city?.toLowerCase().includes(query) ?? false)
      );
    });

  return (
    <div className="mt-10 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b pb-2">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <ClipboardList className="h-5 w-5" /> Puppy Raiser Applications
          {newCount > 0 && (
            <Badge variant="default" className="ml-1">
              {newCount} new
            </Badge>
          )}
        </h2>
        {applications?.length ? (
          <Tabs
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as "all" | AppStatus)}
          >
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="new">New</TabsTrigger>
              <TabsTrigger value="under_review">Under Review</TabsTrigger>
              <TabsTrigger value="accepted">Accepted</TabsTrigger>
              <TabsTrigger value="declined">Declined</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : null}
      </div>

      {isLoading ? (
        <div className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !applications?.length ? (
        <div className="p-6 text-center text-muted-foreground bg-card rounded-xl border">
          No applications yet.
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search by name, email, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {!filtered.length ? (
            <div className="p-6 text-center text-muted-foreground bg-card rounded-xl border">
              {query
                ? `No applications match "${searchQuery.trim()}".`
                : `No ${STATUS_LABELS[statusFilter as AppStatus]?.toLowerCase() ?? statusFilter} applications.`}
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((app) => {
                const status = app.status as AppStatus;
                const expanded = expandedId === app.id;
                return (
                  <Card
                    key={app.id}
                    className={`overflow-hidden ${status === "new" ? "border-primary/40 shadow-sm" : ""}`}
                  >
                    <CardHeader className="bg-muted/30 py-3 px-4 border-b">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm">
                        <div className="font-medium text-primary flex items-center gap-2 flex-wrap">
                          <Badge
                            variant={STATUS_BADGE_VARIANT[status]}
                            className="text-[10px] uppercase tracking-wide"
                          >
                            {STATUS_LABELS[status]}
                          </Badge>
                          <span>
                            {app.fullName}
                            <span className="text-muted-foreground font-normal">
                              {" "}
                              &lt;{app.email}&gt;
                            </span>
                            {app.city && (
                              <span className="text-muted-foreground font-normal">
                                {" "}
                                &middot; {app.city}
                                {app.state ? `, ${app.state}` : ""}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground">
                            {format(new Date(app.createdAt), "MMM d, yyyy")}
                          </span>
                          <Select
                            value={status}
                            onValueChange={(v) =>
                              handleStatusChange(app.id, v as AppStatus)
                            }
                            disabled={updateStatus.isPending}
                          >
                            <SelectTrigger className="h-8 w-[150px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="under_review">
                                Under Review
                              </SelectItem>
                              <SelectItem value="accepted">Accepted</SelectItem>
                              <SelectItem value="declined">Declined</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 space-y-3">
                      <p className="text-sm text-foreground line-clamp-2 whitespace-pre-wrap">
                        {app.whyRaiser}
                      </p>
                      {expanded && <ApplicationDetail app={app} />}
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1"
                          onClick={() =>
                            setExpandedId(expanded ? null : app.id)
                          }
                        >
                          {expanded ? (
                            <>
                              Hide full application{" "}
                              <ChevronUp className="h-4 w-4" />
                            </>
                          ) : (
                            <>
                              View full application{" "}
                              <ChevronDown className="h-4 w-4" />
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1"
                          onClick={() => handlePrint(app)}
                        >
                          Print / Save PDF <Printer className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
